import { useEffect, useRef } from 'react';
import { closeStaleShifts, getWorkspaceId } from '../services/database';

/**
 * Cerrar los turnos que se quedaron abiertos, una vez por taller y sesión.
 *
 * ── El fallo que arregla ────────────────────────────────────────────────────
 *
 * Esto vivía como un `useEffect` suelto dentro de `AppContent`, colgado de
 * `[isAuthorized, isSuperAdmin]`. Es decir: se disparaba en cuanto la sesión
 * quedaba autorizada, que es ANTES de que `loadWorkspaces()` —una ida y vuelta
 * a la red— haya resuelto cuál es el taller. `closeStaleShifts()` llama por
 * dentro a `requireWorkspaceId()`, que lanza mientras el taller sea el
 * centinela `'default'`... y como ninguna de sus dos dependencias vuelve a
 * cambiar, el efecto NO SE REINTENTABA en toda la sesión.
 *
 * Consecuencia real, medida el 22-ago-2026 en 8 personas: quien se olvida de
 * fichar la salida deja una fila abierta que ya nadie cierra, y el índice
 * parcial único `one_open_shift_per_user` le BLOQUEA la siguiente entrada con
 * un error de clave duplicada. El olvido de un día se lleva por delante el
 * fichaje del día siguiente.
 *
 * ── Por qué no basta con esperar a `status === 'resolved'` ──────────────────
 *
 * Está comprobado que no basta, y es la parte contraintuitiva: en
 * `WorkspaceContext`, `setActiveWorkspace(...)` y `setStatus('resolved')`
 * entran en UN SOLO commit de React, y los efectos de los hijos se vacían
 * ANTES que los del padre. El efecto que persiste el id en localStorage vive
 * en el padre. O sea que, en ese commit, la bandera ya dice «resuelto»
 * mientras el localStorage —lo único que `getWorkspaceId()` sabe leer— sigue
 * vacío.
 *
 * Por eso aquí se hacen las dos cosas:
 *
 *  1. Se decide con el id de taller REAL que entra por parámetro (nunca con la
 *     bandera de estado), y `'default'` cuenta como «todavía no se sabe».
 *  2. Antes de llamar se espera a que ese id sea visible para
 *     `getWorkspaceId()`, con una tanda corta y acotada de comprobaciones. La
 *     primera espera ya sale del vaciado de efectos en curso, así que el caso
 *     normal se resuelve en el primer reintento.
 *
 * ── Lo que falta y no es de este archivo ────────────────────────────────────
 *
 * El arreglo limpio es que `closeStaleShifts` acepte el id: en cuanto exista
 * `closeStaleShifts(wsId)` (previsto en el trabajo sobre `services/database.ts`,
 * que tiene otro dueño), esta espera sobra entera — se borra `ESPERAS_MS`, se
 * borra `esperar()` y se llama `closeStaleShifts(wsId)` directamente.
 */

/** El id con el que `WorkspaceContext` rellena el hueco mientras no sabe cuál
 *  es el taller de verdad. Nunca es un taller: es «aún no se sabe». */
export const TALLER_SIN_RESOLVER = 'default';

/** Esperas, en ms, entre comprobaciones de que el id ya se ve desde
 *  `getWorkspaceId()`. La de 0 ms es la que importa: sale del vaciado de
 *  efectos en curso, que es donde estaba la carrera. Las demás cubren un
 *  localStorage lento o inaccesible; al agotarlas se abandona en silencio
 *  (~3,75 s) en vez de dejar un error colgado. */
export const ESPERAS_MS = [0, 50, 200, 500, 1000, 2000];

interface Entradas {
    isAuthorized: boolean;
    isSuperAdmin: boolean;
    /** `activeWorkspace.id` de `useWorkspace()`. */
    wsId: string;
}

export function useCierreDeTurnosVencidos({ isAuthorized, isSuperAdmin, wsId }: Entradas): void {
    // Un taller, un intento. React monta y desmonta los efectos dos veces en
    // desarrollo (StrictMode), y sin esto el cierre saldría por duplicado.
    const yaIntentado = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (!isAuthorized || isSuperAdmin) return;
        // Sin un id de taller REAL no hay nada que cerrar todavía. Al resolverse
        // el taller, `wsId` cambia y el efecto vuelve a correr: eso es lo que
        // antes no pasaba nunca.
        if (!wsId || wsId === TALLER_SIN_RESOLVER) return;
        if (yaIntentado.current.has(wsId)) return;

        let cancelado = false;
        let temporizador: ReturnType<typeof setTimeout> | undefined;

        const intentar = (paso: number) => {
            if (cancelado) return;

            if (getWorkspaceId() !== wsId) {
                // El padre aún no ha persistido el id (o no puede: modo privado,
                // almacenamiento desalojado). Se vuelve a mirar en un momento.
                if (paso >= ESPERAS_MS.length) {
                    console.info('[App] cierre de turnos vencidos omitido: el taller nunca llegó a localStorage');
                    return;
                }
                temporizador = setTimeout(() => intentar(paso + 1), ESPERAS_MS[paso]);
                return;
            }

            yaIntentado.current.add(wsId);
            closeStaleShifts().catch(err => {
                if (!cancelado) console.warn('[App] closeStaleShifts failed:', err?.message || err);
            });
        };

        intentar(0);

        return () => {
            cancelado = true;
            if (temporizador !== undefined) clearTimeout(temporizador);
        };
    }, [isAuthorized, isSuperAdmin, wsId]);
}
